import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { GroupMemberInMonth } from "@/api/nurses";

export interface MonthlyMembershipContextValue {
  memberStatusMap?: Map<string, GroupMemberInMonth>;
  selectedGroupId: string | null;
}

export const MonthlyMembershipContext =
  createContext<MonthlyMembershipContextValue>({
    memberStatusMap: undefined,
    selectedGroupId: null,
  });

export function useMonthlyMembership(nurseId: string) {
  const { memberStatusMap } = useContext(MonthlyMembershipContext);
  return memberStatusMap?.get(nurseId);
}

export type DailyResignationStatus = {
  reason: "퇴사";
  startDate: string;
  endDate: string | null;
};

function toMonthDateISO(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function toDateOnlyISO(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value.trim());
  return match?.[0] ?? null;
}

export function getDailyResignationStatus(
  member: GroupMemberInMonth | null | undefined,
  year: number | null | undefined,
  month: number | null | undefined,
  day: number
): DailyResignationStatus | null {
  if (!member?.resign_date || !year || !month) {
    return null;
  }

  const dateISO = toMonthDateISO(year, month, day);
  const startDate = toDateOnlyISO(member.resign_date);
  if (!startDate) return null;
  if (dateISO < startDate) return null;

  return {
    reason: "퇴사",
    startDate,
    endDate: null,
  };
}

export type MonthlyMembershipProviderProps = MonthlyMembershipContextValue & {
  children: ReactNode;
};
